import { MessageType, type BagChangeCause, type ItemRefusalReason, type ItemRequest } from "odyssey-shared";

/**
 * The inventory frames the page reads (P2.8-T11, ADR-091 decision 20, PROTOCOL.md 6): the server's `bag`, `bagChanged`,
 * `itemRefused` and `bagKept` (P2.8-T49, ADR-125 decision 6.4), each as one event the bag store takes. The text schema has already checked every field when a
 * message reaches here (a slot from 0 to 199, a quantity from 1 to 999, an item id of the rules' pattern, each closed
 * set), so nothing is checked twice.
 */

/**
 * A slot the server holds: its number, its item's rules id and its quantity.
 */
export type HeldSlot = readonly [slot: number, item: string, quantity: number];

/**
 * A slot a change names: what it holds now, or null and 0 for a slot left empty.
 */
export type ChangedSlot = HeldSlot | readonly [slot: number, item: null, quantity: 0];

/**
 * What the server says of the bag: the whole bag with its size, the slots a committed operation changed with what
 * changed them, why a request changed nothing, or which slots hold a stack kept in place, in ascending order.
 */
export type BagEvent =
  | { readonly kind: "bag"; readonly size: number; readonly items: readonly HeldSlot[] }
  | { readonly kind: "kept"; readonly slots: readonly number[] }
  | { readonly kind: "changed"; readonly slots: readonly ChangedSlot[]; readonly cause: BagChangeCause }
  | { readonly kind: "refused"; readonly request: ItemRequest; readonly slot: number; readonly reason: ItemRefusalReason };

/**
 * An inventory frame as the bag store takes it, or null for any other message.
 *
 * @param message A parsed server message.
 * @returns The bag event, or null.
 * @example
 * readBag({ type: MessageType.Bag, size: 20, items: [[0, "minor_healing_potion", 5]] });
 * // { kind: "bag", size: 20, items: [[0, "minor_healing_potion", 5]] }
 */
export function readBag(message: Readonly<Record<string, unknown>> & { readonly type: number }): BagEvent | null {
  switch (message.type) {
    case MessageType.Bag:
      return { kind: "bag", size: message["size"] as number, items: message["items"] as readonly HeldSlot[] };
    case MessageType.BagChanged:
      return { kind: "changed", slots: message["slots"] as readonly ChangedSlot[], cause: message["cause"] as BagChangeCause };
    case MessageType.BagKept:
      return { kind: "kept", slots: message["slots"] as readonly number[] };
    case MessageType.ItemRefused:
      return { kind: "refused", request: message["request"] as ItemRequest, slot: message["slot"] as number, reason: message["reason"] as ItemRefusalReason };
    default:
      return null;
  }
}
